import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MeetingStatusEnum } from "enums/MeetingStatusEnum";
import {
  defaultSkip,
  defaultTake,
  formatUrlParams,
  getBoardRouteKey,
  getDefaultStartDate,
  getIsTemplateFromUrl,
  getMeetingStatusFromUrl,
  getSkipFromUrl,
  getStartDateFromUrl,
  getTakeFromUrl,
  tryFormatDate,
  urlDateFormat,
} from "helpers";
import { createLogger } from "logging";
import { MeetingFilterModel } from "Meetings/models/MeetingFilterModel";
import { useApi, useBoard, useMeeting } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { MeetingsContext } from "./MeetingsContext";

const logger = createLogger("MeetingsProvider");

// Default values
const defaultStartDate = getDefaultStartDate();
const defaultStartDateFormatted = tryFormatDate(defaultStartDate, urlDateFormat);

// use generic getEnumFromUrl from helpers

function getUrlState(filters: MeetingFilterModel) {
  const isTemplate = filters.isTemplate;
  const startDateFormatted = tryFormatDate(filters.startDate, urlDateFormat);
  const hasStartDate = startDateFormatted !== defaultStartDateFormatted;

  const o: Record<string, string | undefined> = {};
  if (!isTemplate && hasStartDate) o.startDate = startDateFormatted;
  if (isTemplate) o.isTemplate = "1";
  if (filters.skip && filters.skip !== defaultSkip) o.skip = filters.skip.toString();
  if (filters.take && filters.take !== defaultTake) o.take = filters.take.toString();
  if (!isTemplate && filters.status) o.status = MeetingStatusEnum[filters.status];

  const params = formatUrlParams(o);
  logger.debug(`getUrlState(): filters:=${JSON.stringify(filters)} -> '${params}'`);

  return [params, o];
}

function setUrlState(filters: MeetingFilterModel) {
  let [url, o] = getUrlState(filters);
  url = url ? `?${url}` : window.location.pathname;
  logger.debug("setUrlState(): newUrl:=", url);
  window.history.pushState(o, "", url);
}

function getDefaultQuery(): MeetingFilterModel {
  return {
    skip: defaultSkip,
    take: defaultTake,
    status: undefined,
    startDate: defaultStartDate,
    isTemplate: false,
  };
}

export const MeetingsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { meetingsApi } = useApi();
  const { board, canEditMeetings } = useBoard();
  const { setSelectMeetingId } = useMeeting();
  const queryClient = useQueryClient();

  // UI state based on permissions
  const showAdditionalData = canEditMeetings;
  const colSpan = 6 + (showAdditionalData ? 5 : 0);

  // Get initial values from URL
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  // State management - unified query object initialized from URL
  const [query, setQuery] = useState<MeetingFilterModel>(() => ({
    startDate: getStartDateFromUrl(urlParams, defaultStartDate),
    isTemplate: getIsTemplateFromUrl(urlParams),
    skip: getSkipFromUrl(urlParams, defaultSkip),
    take: getTakeFromUrl(urlParams, defaultTake),
    status: getMeetingStatusFromUrl(urlParams),
  }));

  const [showAddForm, setShowAddForm] = useState(false);

  const resetQuery = useCallback(() => {
    setQuery(getDefaultQuery());
  }, []);

  // Listen for external URL changes (browser back/forward, manual URL edits)
  useEffect(() => {
    const handlePopState = () => {
      logger.debug("Handling popstate event, updating state from URL");
      const urlParams = new URLSearchParams(window.location.search);
      setQuery((prev) => ({
        ...prev,
        startDate: getStartDateFromUrl(urlParams, defaultStartDate),
        isTemplate: getIsTemplateFromUrl(urlParams),
        skip: getSkipFromUrl(urlParams, defaultSkip),
        take: getTakeFromUrl(urlParams, defaultTake),
        status: getMeetingStatusFromUrl(urlParams),
      }));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Synchronize state changes to URL
  useEffect(() => {
    setUrlState(query);
  }, [query]);

  const boardKey = getBoardRouteKey(board);

  // Load meetings data
  const queryResult = useQuery({
    queryKey: [`Board|${boardKey}|Meetings|${getUrlState(query)[0]}`],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const data = await meetingsApi.getDetailsPaged(query, signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!boardKey,
    refetchOnWindowFocus: true,
  });
  const { refetch } = queryResult;
  const data = queryResult.data;
  const isLoading = queryResult.isFetching || !data;

  // Derived state
  const totalUnfiltered = useMemo(() => data?.totalUnfiltered || -1, [data]);
  const totalFiltered = useMemo(() => data?.totalFiltered || -1, [data]);
  const results = useMemo(() => data?.items || [], [data]);

  // Set the first meeting as selected when items change
  useEffect(() => {
    setSelectMeetingId(results.length > 0 ? results[0].id : -1);
  }, [results, setSelectMeetingId]);

  const reloadMeetings = useCallback(async () => {
    logger.debug(`reloadMeetings(): invalidating query`);
    queryClient.invalidateQueries({ queryKey: [`${boardKey}|Meetings|`] });
    const data = await refetch();
    logger.trace("reloadMeetings(): Loaded", data);
    return data;
  }, [queryClient, boardKey, refetch]);

  // Memoized context value
  const value = useMemo(
    () => ({
      query,
      setQuery,
      resetQuery,
      isLoading,
      results,
      totalFiltered,
      totalUnfiltered,
      showAdditionalData,
      colSpan,
      reloadMeetings,
      showAddForm,
      setShowAddForm,
    }),
    [
      query,
      setQuery,
      resetQuery,
      isLoading,
      results,
      totalFiltered,
      totalUnfiltered,
      showAdditionalData,
      colSpan,
      showAddForm,
      reloadMeetings,
    ]
  );

  return <MeetingsContext.Provider value={value}>{children}</MeetingsContext.Provider>;
};
